import { List } from "@mui/material";
import { FormikProps } from "formik";

import { FileUploadArea } from "../../../../file-upload-area/FileUploadArea";
import { AssetDocumentZone, BankStatementResponse, PendingDocumentMove } from "../../../api/ClientFileApi";
import { useOptionalAssetInventoryDocumentsApi, useOptionalDocumentDndApi } from "../../../api/ClientFileApiContext";
import { IncomeSectionCard } from "../../views/income/components/income-section-card/IncomeSectionCard";
import { BankStatementRow } from "../../views/personal-data/components/bank-statement-upload/BankStatementRow";

export interface AssetDocumentsFormValues {
  documentMoves: PendingDocumentMove[];
}

export type PendingFilesField<T> = { [K in keyof T]: T[K] extends File[] ? K : never }[keyof T] & string;

interface Props<T extends AssetDocumentsFormValues> {
  clientId: string;
  formik: FormikProps<T>;
  fieldName: PendingFilesField<T>;
  zone: AssetDocumentZone;
  title: string;
  uploadHint: string;
  /** An upload area right below the question it belongs to: no title and no card of its own. */
  embedded?: boolean;
  documents: BankStatementResponse[];
  onMarkRead: (documentId: string, isRead: boolean) => void;
  onDelete?: (documentId: string, fileName: string) => void;
  isBusy: boolean;
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function AssetDocumentsSection<T extends AssetDocumentsFormValues>({
  clientId,
  formik,
  fieldName,
  zone,
  title,
  uploadHint,
  embedded,
  documents,
  onMarkRead,
  onDelete,
  isBusy,
  onAddMove,
  onCancelMove,
}: Props<T>) {
  const documentsApi = useOptionalAssetInventoryDocumentsApi();
  const dndApi = useOptionalDocumentDndApi();

  const pendingFiles = formik.values[fieldName] as File[];

  const onDrop = (files: File[]) => {
    void formik.setFieldValue(fieldName, [...pendingFiles, ...files]);
  };

  const onRemovePending = (index: number) => {
    void formik.setFieldValue(
      fieldName,
      pendingFiles.filter((_, i) => i !== index)
    );
  };

  if (!dndApi && !documentsApi) {
    return null;
  }

  const content = dndApi ? (
    <dndApi.AssetDocumentSection
      clientId={clientId}
      zone={zone}
      hint={uploadHint}
      hideTitle={embedded}
      pendingFiles={pendingFiles}
      documentMoves={formik.values.documentMoves}
      onDropFiles={onDrop}
      onRemovePending={onRemovePending}
      onAddMove={onAddMove}
      onCancelMove={onCancelMove}
    />
  ) : (
    <FileUploadArea
      title={embedded ? undefined : title}
      hint={uploadHint}
      onDropFiles={onDrop}
      pendingFiles={pendingFiles}
      onRemovePending={onRemovePending}
    >
      {documents.length > 0 && (
        <List disablePadding sx={{ width: "100%" }}>
          {documents.map((document) => (
            <BankStatementRow
              key={document.documentId}
              statement={document}
              onMarkRead={onMarkRead}
              onDelete={onDelete}
              isBusy={isBusy}
            />
          ))}
        </List>
      )}
    </FileUploadArea>
  );

  if (embedded) {
    return content;
  }

  return (
    <IncomeSectionCard title={title} hideHeading>
      {content}
    </IncomeSectionCard>
  );
}
